import { ApiRoutes } from "@/lib/routeResolver";
import { cache } from "react";
import api from "./api";

// ─── Type Definitions ────────────────────────────────────────────────

export interface Video {
  id: string;
  videoTitle: string;
  videoCode: string;
  filterType: string | null;
  isActive: boolean;
  companyId: string;
  destinationId?: string;
}

interface VideoApiResponse {
  pageNumber: number;
  pageSize: number;
  count: number;
  totalPages: number;
  succeeded: boolean;
  data: Video[];
}

// ─── Data Fetching ───────────────────────────────────────────────────

export const fetchVideos = cache(
  async (
    page = 1,
    pageSize = 9,
    filterType?: string,
    destinationId?: string,
  ): Promise<{ videos: Video[]; totalPages: number; count: number }> => {
    const res = await api.get<VideoApiResponse>(ApiRoutes.WEBSITE_VIDEO, {
      params: {
        PageNumber: page,
        PageSize: pageSize,
        ...(filterType ? { FilterType: filterType } : {}),
        ...(destinationId ? { DestinationId: destinationId } : {}),
      },
    });

    const videos = (res.data?.data ?? []).filter((v) => v.isActive);
    return {
      videos,
      totalPages: res.data?.totalPages ?? 1,
      count: res.data?.count ?? 0,
    };
  },
);

export const fetchVideoFilterTypes = cache(
  async (): Promise<string[]> => {
    const res = await api.get<VideoApiResponse>(ApiRoutes.WEBSITE_VIDEO, {
      params: { PageNumber: 1, PageSize: 100000 },
    });
    const types: string[] = [];
    for (const v of res.data?.data ?? []) {
      if (v.filterType && v.filterType.length > 0 && !types.includes(v.filterType)) {
        types.push(v.filterType);
      }
    }
    return types;
  },
);

// ─── Client-safe fetch (uses NEXT_PUBLIC_ env vars) ─────────────────

const BASE_URL = (process.env.NEXT_PUBLIC_API_URL || "").replace(/\/+$/, "");
const TOKEN = process.env.NEXT_PUBLIC_ACCESS_TOKEN!;
const COMPANY_ID = process.env.NEXT_PUBLIC_COMPANY_ID!;

export async function fetchVideosClient(
  page = 1,
  pageSize = 9,
  filterType?: string,
  destinationId?: string,
): Promise<{ videos: Video[]; totalPages: number; count: number }> {
  const params = new URLSearchParams({
    PageNumber: String(page),
    PageSize: String(pageSize),
    CompanyId: COMPANY_ID,
  });
  if (filterType) params.set("FilterType", filterType);
  if (destinationId) params.set("DestinationId", destinationId);

  const res = await fetch(`${BASE_URL}/website/video?${params}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${TOKEN}`,
    },
  });
  const json = await res.json();
  const videos: Video[] = (json.data ?? []).filter((v: Video) => v.isActive);
  return {
    videos,
    totalPages: json.totalPages ?? 1,
    count: json.count ?? 0,
  };
}
